import CryptoJS from 'crypto-js';

// Get encryption keys from environment variables
const AES_KEY = process.env.REACT_APP_AES_KEY || '';
const AES_IV = process.env.REACT_APP_AES_IV || '';

/**
 * AES Encryption function
 * Encrypts data using AES-128-CBC algorithm
 * @param {string} data - Data to encrypt (should be stringified JSON)
 * @returns {string} - Base64 encoded encrypted data
 */
export const encryptData = (data) => {
  try {
    if (!AES_KEY || !AES_IV) {
      console.error('Encryption keys not configured');
      return data;
    }

    // Convert key and IV to WordArray
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);

    // Encrypt using AES-128-CBC
    const encrypted = CryptoJS.AES.encrypt(data, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return encrypted.toString();
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * AES Decryption function
 * Decrypts data using AES-128-CBC algorithm
 * @param {string} encryptedData - Base64 encoded encrypted data
 * @returns {string} - Decrypted data
 */
export const decryptData = (encryptedData) => {
  try {
    if (!AES_KEY || !AES_IV) {
      console.error('Decryption keys not configured');
      return encryptedData;
    }

    // Convert key and IV to WordArray
    const key = CryptoJS.enc.Utf8.parse(AES_KEY);
    const iv = CryptoJS.enc.Utf8.parse(AES_IV);

    // Decrypt using AES-128-CBC
    const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Decryption error:', error);
    throw new Error('Failed to decrypt data');
  }
};

/**
 * Encrypt request data for API calls
 * @param {object} data - Request data object
 * @returns {object} - Object with encrypted 'req' property
 */
export const encryptRequest = (data) => {
  try {
    const timestamp = Date.now();
    const dataWithTimestamp = {
      ...data,
      e: timestamp.toString()
    };
    
    const jsonString = JSON.stringify(dataWithTimestamp);
    const encrypted = encryptData(jsonString);
    
    return { req: encrypted };
  } catch (error) {
    console.error('Request encryption error:', error);
    throw error;
  }
};

/**
 * Decrypt API response data
 * @param {object} response - API response object
 * @returns {object} - Decrypted response data
 */
export const decryptResponse = (response) => {
  try {
    // Check if response has encrypted data
    if (response?.data?.resDecrypt) {
      return response.data.resDecrypt;
    }
    
    // If response.data is a string (encrypted), decrypt it
    if (typeof response?.data === 'string') {
      const decrypted = decryptData(response.data);
      return JSON.parse(decrypted);
    }
    
    // If already decrypted or no encryption
    return response?.data || response;
  } catch (error) {
    console.error('Response decryption error:', error);
    // Return original response if decryption fails
    return response?.data || response;
  }
};

/**
 * Check if encryption is enabled
 * @returns {boolean}
 */
export const isEncryptionEnabled = () => {
  // Check if encryption is explicitly enabled via environment variable
  const encryptionEnabled = process.env.REACT_APP_ENCRYPTION_ENABLED === 'true';
  const hasKeys = !!(AES_KEY && AES_IV);
  
  return encryptionEnabled && hasKeys;
};

/**
 * Encrypted fetch wrapper - Use this instead of native fetch() for encrypted API calls
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise} - Fetch promise with encrypted request and decrypted response
 */
export const encryptedFetch = async (url, options = {}) => {
  try {
    // Convert URL to string if it's a URL object
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Use original fetch to avoid infinite loop
    const originalFetch = window.originalFetch || window.fetch;
    
    // Skip encryption for admin API and external APIs
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'apis.garudaambo.com';
    const adminBaseUrl = process.env.REACT_APP_ADMIN_API_BASE_URL || 'admin.garudaambo.com';
    
    // Check if URL is admin API (should NOT be encrypted)
    const isAdminAPI = urlString.includes(adminBaseUrl) || 
                       urlString.includes('admin.garudaambo.com');
    
    // Check if URL is external API (should NOT be encrypted)
    const isExternalAPI = !urlString.includes(apiBaseUrl) && 
                          !urlString.includes('apis.garudaambo.com');
    
    if (isAdminAPI || isExternalAPI || !isEncryptionEnabled()) {
      // Use regular fetch for admin API, external APIs, or when encryption is disabled
      return originalFetch(urlString, options);
    }

    // Extract api_key and user_id from URL and move to body
    let finalUrl = urlString;
    let apiKey = null;
    let userId = null;
    
    try {
      const urlObj = new URL(urlString);
      const params = urlObj.searchParams;
      
      // Extract api_key and user_id
      apiKey = params.get('api_key');
      userId = params.get('user_id');
      
      // Remove from URL
      if (apiKey) params.delete('api_key');
      if (userId) params.delete('user_id');
      
      // Rebuild URL without api_key and user_id
      finalUrl = urlObj.origin + urlObj.pathname + (params.toString() ? '?' + params.toString() : '');
    } catch (e) {
      // If URL parsing fails, continue with original URL
      console.warn('URL parsing failed:', e);
    }

    // Parse existing body if present
    let requestData = {};
    if (options.body) {
      // Check if body is FormData - cannot be encrypted directly
      if (options.body instanceof FormData) {
        console.error('❌ FormData cannot be encrypted directly. Please convert to JSON object.');
        console.error('   Example: Instead of FormData, use: { key: value, ... }');
        console.error('   URL:', urlString);
        throw new Error('FormData is not supported with encryption. Use JSON object instead.');
      }
      
      try {
        requestData = typeof options.body === 'string' ? JSON.parse(options.body) : options.body;
      } catch (e) {
        // If body is not JSON, skip encryption
        console.warn('Body is not JSON, skipping encryption for:', urlString);
        return originalFetch(urlString, options);
      }
    }

    // Add api_key and user_id to request body
    if (apiKey) requestData.api_key = apiKey;
    if (userId) requestData.user_id = userId;

    // Encrypt the request data
    const encryptedData = encryptRequest(requestData);

    // Update options with encrypted body
    const encryptedOptions = {
      ...options,
      body: JSON.stringify(encryptedData),
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      }
    };

    // Make the fetch call using original fetch with cleaned URL
    const response = await originalFetch(finalUrl, encryptedOptions);
    
    // Clone response to read it multiple times
    const clonedResponse = response.clone();
    
    // Try to parse and decrypt response
    try {
      const responseText = await response.text();
      
      // Try to parse as JSON first
      let responseData;
      try {
        responseData = JSON.parse(responseText);
      } catch (e) {
        // If not JSON, assume it's encrypted string
        responseData = responseText;
      }
      
      // If response is an encrypted string (not an object), decrypt it
      if (typeof responseData === 'string' && responseData.length > 0) {
        try {
          const decrypted = decryptData(responseData);
          const decryptedData = JSON.parse(decrypted);
          
          // Wrap in resDecrypt format for compatibility with existing code
          const wrappedResponse = {
            resDecrypt: decryptedData
          };
          
          return new Response(JSON.stringify(wrappedResponse), {
            status: clonedResponse.status,
            statusText: clonedResponse.statusText,
            headers: clonedResponse.headers
          });
        } catch (decryptError) {
          console.error('Decryption failed:', decryptError);
          // Return original response if decryption fails
          return clonedResponse;
        }
      }
      
      // If response already has resDecrypt (backend not encrypting response)
      if (responseData && responseData.resDecrypt) {
        return new Response(JSON.stringify(responseData), {
          status: clonedResponse.status,
          statusText: clonedResponse.statusText,
          headers: clonedResponse.headers
        });
      }
      
      // Return original response
      return new Response(JSON.stringify(responseData), {
        status: clonedResponse.status,
        statusText: clonedResponse.statusText,
        headers: clonedResponse.headers
      });
    } catch (error) {
      console.error('Response processing error:', error);
      // If processing fails, return original response
      return clonedResponse;
    }
  } catch (error) {
    console.error('Encrypted fetch error:', error);
    throw error;
  }
};

/**
 * Replace global fetch with encrypted version
 * Call this once in your app entry point (index.js or App.js)
 */
export const enableGlobalEncryption = () => {
  // Check if encryption is enabled via environment variable
  if (process.env.REACT_APP_ENCRYPTION_ENABLED !== 'true') {
    console.log('🔓 Encryption is DISABLED (REACT_APP_ENCRYPTION_ENABLED=false)');
    console.log('   To enable: Set REACT_APP_ENCRYPTION_ENABLED="true" in .env');
    return;
  }
  
  if (!isEncryptionEnabled()) {
    console.warn('⚠️ Encryption keys not configured. Global encryption not enabled.');
    console.warn('   Add REACT_APP_AES_KEY and REACT_APP_AES_IV to .env');
    return;
  }

  // Store original fetch ONCE
  if (window.originalFetch) {
    console.warn('Global encryption already enabled');
    return;
  }
  
  window.originalFetch = window.fetch;

  // Replace global fetch
  window.fetch = async (url, options) => {
    // Get the URL as a string
    const urlString = typeof url === 'string' ? url : url.toString();
    
    // Check if this is an API call that needs encryption
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'apis.garudaambo.com';
    
    // Only encrypt main API, NOT admin API
    const needsEncryption = 
      urlString.includes(apiBaseUrl) ||
      urlString.includes('apis.garudaambo.com');

    if (needsEncryption) {
      return encryptedFetch(urlString, options);
    }

    // Use original fetch for non-API calls (including admin API)
    return window.originalFetch(url, options);
  };

  console.log('🔐 Global encryption ENABLED for all API calls');
  console.log('   - Encrypting:', process.env.REACT_APP_API_BASE_URL || 'apis.garudaambo.com');
  console.log('   - Encrypting:', process.env.REACT_APP_ADMIN_API_BASE_URL || 'admin.garudaambo.com');
  console.log('   - To disable: Set REACT_APP_ENCRYPTION_ENABLED="false" in .env');
};
